// hooks/api/techSkillVendor.queries.ts
import { notifyError, notifySuccess } from "@/lib/notification";
import * as techSkillVendorService from "@/services/techSkillVendor.service";
import {
  TechSkillVendorCreateType,
  TechSkillVendorParamsType,
  TechSkillVendorType,
} from "@/services/techSkillVendor.service";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";

export const techSkillVendorsKey = {
  all: ["techSkillVendors"] as const,
  lists: (filters: TechSkillVendorParamsType) =>
    [...techSkillVendorsKey.all, "lists", filters] as const,
  detail: (id: string) => [...techSkillVendorsKey.all, "detail", id] as const,
};

export const useTechSkillVendorsQuery = (filters: TechSkillVendorParamsType) =>
  useQuery({
    queryKey: techSkillVendorsKey.lists(filters),
    queryFn: () => techSkillVendorService.fetchAllTechSkillVendors(filters),
    placeholderData: (prev) => prev,
  });

export const useTechSkillVendorByIdQuery = (id: string, enabled = true) =>
  useQuery({
    queryKey: techSkillVendorsKey.detail(id),
    queryFn: () => techSkillVendorService.fetchTechSkillVendorById(id),
    enabled: enabled && !!id,
  });

export const useCreateTechSkillVendorMutation = (
  filters: TechSkillVendorParamsType
) => {
  const queryClient = useQueryClient();
  return useMutation<
    TechSkillVendorType,
    AxiosError,
    TechSkillVendorCreateType
  >({
    mutationFn: (data) => techSkillVendorService.createTechSkillVendor(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: techSkillVendorsKey.lists(filters),
      });
      notifySuccess("Tech skill vendor added successfully");
    },
    onError: (error: any) =>
      notifyError(
        error?.response?.data?.message || "Failed to add tech skill vendor"
      ),
  });
};

export const useUpdateTechSkillVendorMutation = (
  filters: TechSkillVendorParamsType
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      _id,
      data,
    }: {
      _id: string;
      data: TechSkillVendorCreateType;
    }) => techSkillVendorService.updateTechSkillVendor(_id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: techSkillVendorsKey.lists(filters),
      });
      queryClient.invalidateQueries({
        queryKey: techSkillVendorsKey.detail(variables._id),
      });
      notifySuccess("Tech skill vendor updated successfully");
    },
    onError: (error: any) =>
      notifyError(
        error?.response?.data?.message || "Failed to update tech skill vendor"
      ),
  });
};

export const useDeleteTechSkillVendorMutation = (
  filters: TechSkillVendorParamsType
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (_id: string) =>
      techSkillVendorService.deleteTechSkillVendor(_id),
    onSuccess: (_, _id) => {
      queryClient.invalidateQueries({
        queryKey: techSkillVendorsKey.lists(filters),
      });
      queryClient.removeQueries({ queryKey: techSkillVendorsKey.detail(_id) });
      notifySuccess("Tech skill vendor deleted successfully");
    },
    onError: (error: any) =>
      notifyError(
        error?.response?.data?.message || "Failed to delete tech skill vendor"
      ),
  });
};
